import { lazy, StrictMode } from "react"

import { LazyMotion, MotionConfig } from "motion/react"
import { createRoot } from "react-dom/client"

import Analytics from "@/lib/Analytics"
import Tracing from "@/lib/Tracing"

import VersionManager from "@/components/VersionManager"

import App from "@/App"
import DragTypeContextProvider from "@/context/DragTypeContext"
import VersionReporter from "@/VersionReporter"

import "@/index.css"

const MIXPANEL_TOKEN = import.meta.env.VITE_PUBLIC_MIXPANEL_TOKEN
const SENTRY_DSN = import.meta.env.VITE_PUBLIC_SENTRY_DSN

if (MIXPANEL_TOKEN) {
  Analytics.init(MIXPANEL_TOKEN, { track_pageview: "url-with-path-and-query-string", persistence: "localStorage" })
  Analytics.register({
    environment: import.meta.env.VITE_PUBLIC_ENVIRONMENT || "unknown",
    desktop_appversion: typeof __APP_VERSION__ === "undefined" ? "" : __APP_VERSION__,
  })
}

if (SENTRY_DSN) {
  /* global __APP_VERSION__ */
  Tracing.init({
    dsn: SENTRY_DSN,
    release: __APP_VERSION__,
    environment: import.meta.env.VITE_PUBLIC_ENVIRONMENT,
  })
}

const ToastContextProvider = lazy(() =>
  import("@/context/ToastContext").then((mod) => ({ default: mod.ToastContextProvider }))
)
const loadFeatures = () => import("@/lib/Motion").then((mod) => mod.default)

declare global {
  interface Window {
    fcmSW: ServiceWorkerRegistration | null
  }
}

if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register(import.meta.env.MODE === "production" ? "/firebase-messaging-sw.js" : "/src/firebase-messaging-sw.ts", {
      type: "module",
    })
    .then((registration) => {
      window.fcmSW = registration
    })
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <LazyMotion features={loadFeatures} strict>
      <MotionConfig transition={{ type: "tween", ease: [0.3, 0, 0.2, 1], duration: 0.3 }}>
        <ToastContextProvider>
          <DragTypeContextProvider>
            <App />
          </DragTypeContextProvider>
        </ToastContextProvider>
      </MotionConfig>
    </LazyMotion>
    <VersionReporter />
    <VersionManager />
  </StrictMode>
)
